import React, { useMemo } from 'react';
import { Icon } from 'antd';
import cn from 'classnames';
import Btn, { btnTypes } from '../../../btn';
import { selectors, stateKeys } from '../../model';
import { useStoreSelector } from '../../hooks/useStoreSelector';
import { getSelectedOptionsContent, isFn } from '../../utils';
import { useValueContext } from '../../contexts';
import styles from './Control.module.scss';

const { selectValue } = selectors;

const getSelector = (key) => (state) => selectValue(state, key);

const labelSelector = getSelector(stateKeys.label);
const loadingSelector = getSelector(stateKeys.loading);
const disabledSelector = getSelector(stateKeys.disabled);
const visibleSelector = getSelector(stateKeys.visible);
const renderControlSelector = getSelector(stateKeys.renderControl);
const renderSelectedOptionSelector = getSelector(
  stateKeys.renderSelectedOption
);
const classNameSelector = getSelector(stateKeys.className);
const multipleSelector = getSelector(stateKeys.multiple);
const optionsSelector = getSelector(stateKeys.options);
const currentValueSelector = getSelector(stateKeys.currentValue);
const placeholderSelector = getSelector(stateKeys.placeholder);

const MultipleControl = ({ children, ...props }) => {
  const label = useStoreSelector(labelSelector) || 'Выберите из списка';
  const value = useValueContext();

  return (
    <Btn {...props}>
      <span className={styles.leftCol}>
        <span className={styles.placeholder}>{label}</span>
        <span className={styles.amount}>Выбрано: {value?.length || 0}</span>
      </span>
      {children}
    </Btn>
  );
};

const contentCreators = {
  withLabel: ({ label, content, placeholder }) => (
    <span className={styles.leftCol}>
      <span className={styles.placeholder}>{label}</span>
      <span className={content ? styles.amount : styles.placeholder}>
        {content ?? placeholder}
      </span>
    </span>
  ),

  contentOnly: ({ content, placeholder }) => (
    <span className={content ? styles.amount : styles.placeholder}>
      {content ?? placeholder}
    </span>
  ),
};

const SingleControl = ({ children, ...props }) => {
  const label = useStoreSelector(labelSelector);
  const renderSelectedOption = useStoreSelector(renderSelectedOptionSelector);
  const options = useStoreSelector(optionsSelector);
  const value = useStoreSelector(currentValueSelector);
  const placeholder =
    useStoreSelector(placeholderSelector) || 'Выберите из списка...';

  const content = useMemo(
    () => getSelectedOptionsContent({ options, value, renderSelectedOption }),
    [options, value, renderSelectedOption]
  );

  const renderedContent = label
    ? contentCreators.withLabel({ label, content, placeholder })
    : contentCreators.contentOnly({ content, placeholder });

  return (
    <Btn {...props}>
      {renderedContent}
      {children}
    </Btn>
  );
};

const DefaultControl = ({
  className,
  visible,
  loading,
  disabled,
  ...props
}) => {
  const multiple = useStoreSelector(multipleSelector);

  const Container = useMemo(
    () => (multiple ? MultipleControl : SingleControl),
    [multiple]
  );

  return (
    <Container
      {...props}
      disabled={disabled || loading}
      className={cn(styles.root, className)}
      type={btnTypes.secondary}
      block
    >
      <Icon
        type={loading ? 'loading' : 'down'}
        className={cn(styles.icon, { [styles.visible]: visible })}
      />
    </Container>
  );
};

const Control = (props) => {
  const renderControl = useStoreSelector(renderControlSelector);
  const loading = useStoreSelector(loadingSelector);
  const disabled = useStoreSelector(disabledSelector);
  const visible = useStoreSelector(visibleSelector);
  const className = useStoreSelector(classNameSelector);

  return isFn(renderControl) ? (
    renderControl({ ...props, className, visible, loading, disabled })
  ) : (
    <DefaultControl
      {...props}
      className={className}
      loading={loading}
      disabled={disabled}
      visible={visible}
    />
  );
};

export default Control;
